import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { Order } from '../api/types'
import { Empty, ErrorBanner, Loading, Panel, StatusChip, dateTime, inr, num } from '../components/ui'

type Filter = 'all' | 'open' | 'completed' | 'cancelled'

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
]

function matches(order: Order, filter: Filter): boolean {
  switch (filter) {
    case 'open':
      return !['completed', 'cancelled'].includes(order.status)
    case 'completed':
      return order.status === 'completed'
    case 'cancelled':
      return order.status === 'cancelled'
    default:
      return true
  }
}

function place(address: { name?: string; city?: string } | null): string {
  if (!address) return '—'
  return address.city ?? address.name ?? '—'
}

export default function OrdersPage() {
  const [filter, setFilter] = useState<Filter>('all')

  const ordersQuery = useQuery({
    queryKey: ['portal-orders'],
    queryFn: () => api.get<Order[]>('/portal/orders').then((r) => r.data),
  })

  const all = ordersQuery.data ?? []
  const shown = all.filter((order) => matches(order, filter))

  return (
    <div className="stack">
      <div className="page-head">
        <div className="page-title">Orders</div>
        <div className="page-sub">
          What you asked the transporter to move, before it became a consignment on a truck
        </div>
      </div>

      <div className="filters">
        {FILTERS.map((option) => (
          <button
            key={option.key}
            className={`filter-chip ${filter === option.key ? 'active' : ''}`}
            onClick={() => setFilter(option.key)}
          >
            {option.label} ({all.filter((o) => matches(o, option.key)).length})
          </button>
        ))}
      </div>

      <Panel title={`${shown.length} order(s)`} bodyless>
        {ordersQuery.isLoading ? (
          <Loading />
        ) : ordersQuery.error ? (
          <div style={{ padding: 16 }}>
            <ErrorBanner error={ordersQuery.error} />
          </div>
        ) : shown.length === 0 ? (
          <Empty
            title="No orders"
            hint={filter === 'all' ? 'Orders placed with this transporter appear here.' : 'Try another filter.'}
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Order</th>
                {/* Their PO number, not ours — it is the one a customer can
                    actually match against their own paperwork. */}
                <th>Your reference</th>
                <th>Route</th>
                <th>Deliver by</th>
                <th>Status</th>
                <th className="num">Weight</th>
                <th className="num">COD</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((order) => (
                <tr key={order.id}>
                  <td data-label="Order" className="mono">
                    {order.order_number}
                  </td>
                  <td data-label="Your reference">{order.customer_reference ?? '—'}</td>
                  <td data-label="Route">
                    {place(order.pickup_address_json)} → {place(order.delivery_address_json)}
                  </td>
                  <td data-label="Deliver by">{dateTime(order.deliver_by)}</td>
                  <td data-label="Status">
                    <StatusChip status={order.status} />
                  </td>
                  <td data-label="Weight" className="num">
                    {num(order.weight_kg, 0)} kg
                  </td>
                  <td data-label="COD" className="num">
                    {Number(order.cod_amount) > 0 ? inr(order.cod_amount) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}
